import type React from "react";
import type { TFunction } from "i18next";

import { IS_MAC } from "@/__main__/constants.mjs";
import { appURLs } from "@/app/constants.mjs";
import type { GamesWithProp, GameSymbol } from "@/app/games.mjs";
import gamesList from "@/app/games.mjs";
import LoLDefinition from "@/game-lol/definition.mjs";
import TFTDefinition, { GAME_SYMBOL_TFT } from "@/game-tft/definition.mjs";
// import ValorantDefinition from "@/game-val/definition.mjs";
import SymbolMap from "@/util/symbol-map.mjs";

export type OverlaySettingKey = readonly string[];

interface OverlayFeature<C, D> {
  key: OverlaySettingKey;
  disabled?: (settings?: C) => boolean;
  onChange?: (value: D, settings: C) => void;
}

type OverlayFeatureHotkey<C = Record<string, unknown>> = {
  key: OverlaySettingKey;
  disabled?: (settings?: C) => boolean;
} & OverlayFeature<C, string>;

export type OverlayFeatureToggle<C = Record<string, unknown>> = {
  key: OverlaySettingKey;
  inverse?: true;
  disabled?: (settings?: C) => boolean;
  onChange?: (value: boolean, settings: C) => void;
} & OverlayFeature<C, boolean>;
export type OverlayFeatureSelect<C = Record<string, unknown>> = {
  key: OverlaySettingKey;
  value?: AnyFunction;
  aspect?: number;
  style?: "radio" | "thumbnail";
  options: {
    text?: Translation;
    description?: Translation;
    value?: string | boolean | number;
    image?: string;
    Icon?: SVGComponent;
    layoutBias?: number;
    disabled?: () => boolean;
  }[];
} & OverlayFeature<C, string | boolean | number>;

export type GameOverlayFeature<C = Record<string, unknown>> = {
  title?: Translation;
  description?: Translation;
  text?: Translation;
  hint?: Translation & { icon?: SVGComponent };
  toggle?: OverlayFeatureToggle<C>;
  hotkey?: OverlayFeatureHotkey<C>;
  select?: OverlayFeatureSelect<C>;
  items?: GameOverlayFeature<C>[];
  divider?: "start" | "end";
  renderProButton?: (t: TFunction) => React.ReactNode;
  disabled?: (settings: C) => boolean;
  show?: (settings: C) => boolean;
  requiresPro?: boolean;
  freeTrialDate?: Date;
};

// Do not use this one!! For defining base constants only
export interface BaseGameOverlay<C = Record<string, unknown>> {
  key?: OverlaySettingKey;
  title: Translation;
  tags: string[];

  // because there's not an easy way to differentiate between React.FC
  // and function returning Promise...
  Preview?: React.FC;

  partlyFree?: boolean;
  description: Translation;
  conflictDisclaimer?: Translation;
  // A preferred translation for short form display
  brief?: Translation;
  disabled?: (settings: C) => boolean;
  features?: GameOverlayFeature<C>[] | (() => Promise<GameOverlayFeature<C>[]>);
  images: {
    large: string;
    small: string;
    settings?: string;
    featured?: string;
  };
  /// Overlay is rendered natively (no web component)
  native?: boolean;
  date: Date;
  noToggle?: boolean;
  onToggle?: (overlay: BaseGameOverlay, enabled: boolean) => void;
  // Notify something about this overlay to users with a banner.
  notification?: Translation;
}

// use this one :)
export interface GameOverlay<C = Record<string, unknown>>
  extends BaseGameOverlay<C> {
  game: symbol;
  /// `id`: a reference to the overlay id (string) in the map, used for quick lookups.
  /// `id` is NOT guaranteed to be unique across games
  id: string;
  isActive?: boolean;
}

export const IS_OVERLAYS_OS = !IS_MAC;

export const GAME_OVERLAY_BACKGROUNDS = new SymbolMap({
  ...gamesList.reduce(
    (obj, game) => {
      if ("overlayBackground" in game) {
        obj[game.gameSymbol] = game.overlayBackground.url;
      }

      return obj;
    },
    {} as Record<GamesWithProp<"overlayBackground">["gameSymbol"], string>,
  ),

  // TODO: move these into GameDefinition
  [GAME_SYMBOL_TFT]: `${appURLs.CDN}/blitz/ui/img/settings/tftOverlay/tft-background.webp`,
});

export const GAME_OVERLAY_BACKGROUNDS_OFFSET = new SymbolMap({
  ...gamesList.reduce(
    (obj, game) => {
      if ("overlayBackground" in game) {
        obj[game.gameSymbol] = game.overlayBackground.offset;
      }

      return obj;
    },
    {} as Record<GamesWithProp<"overlayBackground">["gameSymbol"], string>,
  ),
});

// Here we are just adding props to each overlay to promote BaseGameOverlay to GameOverlay
const overlayEntry = <S extends symbol>(
  game: S,
  overlays: Record<string, BaseGameOverlay>,
): [S, Record<string, GameOverlay>] => [
  game,
  Object.assign(
    overlays,
    Object.fromEntries(
      Object.entries(overlays).map(([id, overlay]) => [
        id,
        Object.assign(overlay, {
          game,
          id,
        }),
      ]),
    ),
  ),
];

export const GAME_OVERLAYS = SymbolMap.fromEntries<
  GameSymbol,
  Record<string, GameOverlay>
>([
  ...gamesList
    .filter(
      (game): game is GamesWithProp<"overlays"> =>
        "overlays" in game && !!game.overlays,
    )
    .map(({ gameSymbol, overlays }) => {
      return overlayEntry(gameSymbol, overlays);
    }),
]);

// Streamer overlays aren't actually overlays in the regular sense that
// they appear in-game, but rather as a separate browser instance that is
// composited on top of a stream, usually via OBS Studio.
export const STREAMER_OVERLAYS = new SymbolMap({
  ...gamesList.reduce(
    (obj, game) => {
      if ("streamerOverlays" in game) {
        obj[game.gameSymbol] = game.streamerOverlays;
        assignOverlayKeys(game.streamerOverlays, game.gameSymbol);
      }

      return obj;
    },
    {} as Record<
      GamesWithProp<"streamerOverlays">["gameSymbol"],
      Record<string, BaseGameOverlay>
    >,
  ),
});

function assignOverlayKeys(obj, gameSymbol) {
  for (const key in obj) {
    const value = obj[key];
    value.id = key;
    value.game = gameSymbol;
  }
}

// SymbolMap<Record<typeof GAME_SYMBOL_APEX, Record<string, GameOverlay<Record<string, unknown>>>>>

export const OVERLAY_GAMES = SymbolMap.keys(GAME_OVERLAYS);

export const GAME_OVERLAY_SETTINGS = new SymbolMap({
  ...gamesList.reduce(
    (obj, game) => {
      if ("overlaySettings" in game) {
        obj[game.gameSymbol] = game.overlaySettings;
      }

      return obj;
    },
    {} as Record<GamesWithProp<"overlayBackground">["gameSymbol"], unknown>,
  ),
});

export const OVERLAY_TAGS = {
  new: {
    text: ["overlays:tags.new", "New"],
    color: "var(--turq)",
  },
  tracker: {
    text: ["overlays:tags.tracker", "Tracker"],
  },
  utility: {
    text: ["overlays:tags.utility", "Utility"],
  },
  performance: {
    text: ["overlays:tags.performance", "Performance"],
  },
  coaching: {
    text: ["overlays:tags.coaching", "Coaching"],
  },
  comingSoon: {
    text: ["common:comingSoonNormal", "Coming Soon"],
  },
} as const;

export const OVERLAY_TAG_FALLBACK_COLOR = "var(--shade1)";

export const FEATURED_OVERLAYS = [
  TFTDefinition.overlays.compositions,
  // ValorantDefinition.overlays.agentSelect,
  LoLDefinition.overlays.skillRecommended,
] as const;

// refs
export default {
  GAME_OVERLAYS,
  OVERLAY_TAGS,
  FEATURED_OVERLAYS,
};
